<template>
  <meals-table :data="data" :loading="isLoading" :search-query="searchQuery" />
</template>

<script>
import MealsTable from "@/components/guest/MealsTable.vue";
import reportService from "@/services/report";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import { joinPrilog } from "@/utils/guestPrilog";

export default {
  name: "MealsTab",
  mixins: [hasPrinting, hasExcel],
  components: {
    MealsTable,
  },
  props: {
    searchQuery: String,
    dateRange: Array,
    isActiveTab: Boolean,
  },
  data() {
    return {
      data: [],
      isLoading: false,
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("food"), value: "name", sortable: false },
        {
          text: this.$t("quantity"),
          value: "quantity",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("notes"),
          value: "prilog",
          sortable: false,
        },
      ];
    },
    restaurantId() {
      return this.$store.state.restaurant?.id;
    },
    totalQuantity() {
      return this.data.reduce((sum, item) => sum + item.quantity, 0);
    },
  },
  watch: {
    isPrinting(isPrinting) {
      if (!isPrinting || !this.isActiveTab || !this.data || this.data.length === 0) {
        return;
      }

      this.printMealsTable();
    },
    isExcelDownloading(isDownloading) {
      if (!isDownloading || !this.isActiveTab || !this.data || this.data.length === 0) {
        return;
      }

      this.exportMealsExcel();
    },
    dateRange: {
      handler() {
        this.loadData();
      },
      immediate: true,
    },
  },
  methods: {
    async loadData() {
      if (!this.restaurantId || !this.dateRange || this.dateRange.length !== 2) {
        return;
      }

      this.isLoading = true;
      try {
        const params = {
          date: this.dateRange[0],
          date2: this.dateRange[1],
        };

        const response = await reportService.getRestaurantGuest(
          this.restaurantId,
          params
        );
        this.processData(response.data);
      } catch (error) {
        console.error("Error loading meals data:", error);
        this.data = [];
      } finally {
        this.isLoading = false;
      }
    },
    processData(data) {
      const foodsMap = {};

      // Iterate through all shifts
      Object.keys(data).forEach((shift) => {
        const shiftData = data[shift];

        // Iterate through all parent meals in the shift
        Object.keys(shiftData).forEach((mealName) => {
          const parentMeal = shiftData[mealName];
          const parentQuantity = parentMeal.quantity;

          // Get prilog text from the parent prilog array
          const prilogText = joinPrilog(parentMeal.prilog);

          // Iterate through all foods in this parent meal
          if (parentMeal.foods && Array.isArray(parentMeal.foods)) {
            parentMeal.foods.forEach((food) => {
              const foodId = food.id;

              if (foodsMap[foodId]) {
                // Food already exists, add quantity
                foodsMap[foodId].quantity += parentQuantity;
                // Merge prilog if different
                if (prilogText && !foodsMap[foodId].prilog.includes(prilogText)) {
                  foodsMap[foodId].prilog = foodsMap[foodId].prilog
                    ? `${foodsMap[foodId].prilog}, ${prilogText}`
                    : prilogText;
                }
              } else {
                // New food, add to map
                foodsMap[foodId] = {
                  id: food.id,
                  name: food.name,
                  quantity: parentQuantity,
                  prilog: prilogText,
                };
              }
            });
          }
        });
      });

      // Convert map to array and sort by name
      this.data = Object.values(foodsMap).sort((a, b) => a.name.localeCompare(b.name));
    },
    printMealsTable() {
      const tableItems = this.data.map((item, index) => ({
        num: index + 1,
        name: this.toLatin(item.name),
        quantity: item.quantity,
        prilog: item.prilog || "",
      }));

      // Add total row
      tableItems.push({
        num: "",
        name: `<strong>${this.$t("total").toUpperCase()}</strong>`,
        quantity: `<strong>${this.totalQuantity}</strong>`,
        prilog: "",
      });

      this.printDocument({
        pageTitle: this.$t("routes.jela"),
        title: this.$t("routes.jela"),
            subtitle: `${this.localeDate(this.dateRange[0])} - ${this.localeDate(
              this.dateRange[1]
            )}`,
        tables: [
          {
            headers: this.headers.map((h) => ({
              text: h.text,
              value: h.value,
              align: h.align,
            })),
            items: tableItems,
          },
        ],
      });
    },
    exportMealsExcel() {
      const tableItems = this.data.map((item, index) => ({
        num: index + 1,
        name: this.toLatin(item.name),
        quantity: item.quantity,
        prilog: item.prilog || "",
      }));

      // Add total row
      tableItems.push({
        num: "",
        name: this.$t("total").toUpperCase(),
        quantity: this.totalQuantity,
        prilog: "",
      });

      this.downloadExcel({
        fileName: `jela_${this.dateRange[0]}_${this.dateRange[1]}.xlsx`,
        sheets: [
          {
            name: this.$t("routes.jela"),
            title: this.$t("routes.jela"),
            subtitle: `${this.localeDate(this.dateRange[0])} - ${this.localeDate(
              this.dateRange[1]
            )}`,
            headers: this.headers.map((h) => ({
              text: h.text,
              value: h.value,
              align: h.align,
            })),
            items: tableItems,
          },
        ],
      });
    },
  },
};
</script>
